import { createContext, useCallback, useContext, useMemo, useReducer } from 'react'
import * as transactionService from '../services/transactionService'
import { getUserFacingErrorMessage } from '../utils/userFacingError'
import {
  TransactionActionType,
  createInitialTransactionState,
  transactionReducer,
} from './reducers/transactionReducer'

const TransactionContext = createContext<any>(null)

export function TransactionProvider({ children }) {
  const [state, dispatch] = useReducer(
    transactionReducer,
    undefined,
    createInitialTransactionState,
  )

  const fetchTransactions = useCallback(
    async (
      params: transactionService.TransactionsListParams = {},
      opts: { totalsOnly?: boolean } = {},
    ) => {
      if (!opts.totalsOnly) dispatch({ type: TransactionActionType.LOAD_LIST_START })
      try {
        const data = await transactionService.fetchTransactions(params)
        if (opts.totalsOnly) {
          dispatch({ type: TransactionActionType.SET_LIST_TOTAL, payload: data.total })
        } else {
          dispatch({ type: TransactionActionType.LOAD_LIST_SUCCESS, payload: data })
        }
        return data
      } catch (e) {
        dispatch({
          type: TransactionActionType.LOAD_LIST_FAILURE,
          payload: getUserFacingErrorMessage(e, 'Failed to load transactions'),
        })
        throw e
      }
    },
    [],
  )

  const getTransactionById = useCallback(async (id) => {
    try {
      const item = await transactionService.fetchTransactionById(id)
      dispatch({ type: TransactionActionType.SET_SELECTED, payload: item })
      return item
    } catch (e) {
      dispatch({
        type: TransactionActionType.SET_ERROR,
        payload: getUserFacingErrorMessage(e, 'Failed to load transaction'),
      })
      throw e
    }
  }, [])

  const clearError = useCallback(() => {
    dispatch({ type: TransactionActionType.CLEAR_ERROR })
  }, [])

  const value = useMemo(
    () => ({
      transactions: state.transactions,
      listTotal: state.listTotal,
      listPage: state.listPage,
      listLimit: state.listLimit,
      selectedTransaction: state.selectedTransaction,
      listLoading: state.listLoading,
      error: state.error,
      fetchTransactions,
      getTransactionById,
      clearError,
    }),
    [state, fetchTransactions, getTransactionById, clearError],
  )

  return <TransactionContext.Provider value={value}>{children}</TransactionContext.Provider>
}

export function useTransaction() {
  const ctx = useContext(TransactionContext)
  if (!ctx) throw new Error('useTransaction must be used within TransactionProvider')
  return ctx
}
